import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, X, MapPin, GripVertical } from 'lucide-react';
import toast from 'react-hot-toast';
import ImageUpload from '../components/ImageUpload';
import {
  getDestinations, addDestination, updateDestination, deleteDestination
} from '../firebase/firestore';

const EMPTY = {
  city: '', country: '', price: 'From $299',
  imageUrl: '', order: 1,
  color1: 0xFF1A3C8F, color2: 0xFF2D5BE3,
};

export default function FeaturedDestinations() {
  const [dests, setDests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [imageFile, setImageFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    getDestinations().then(d => { setDests(d); setLoading(false); });
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm({ ...EMPTY, order: dests.length + 1 }); setImageFile(null); setModal('add'); };
  const openEdit = d => { setForm({ ...d }); setImageFile(null); setModal(d); };
  const close = () => { setModal(null); setImageFile(null); setUploadProgress(0); };
  const f = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const save = async () => {
    if (!form.city || !form.country) { toast.error('City and country required'); return; }
    setSaving(true);
    try {
      if (modal === 'add') {
        await addDestination({ ...form, featured: true }, imageFile, setUploadProgress);
        toast.success('Destination added — will appear in app home screen');
      } else {
        await updateDestination(modal.id, { ...form, featured: true }, imageFile, setUploadProgress);
        toast.success('Destination updated');
      }
      close(); load();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const remove = async d => {
    if (!window.confirm(`Remove ${d.city} from featured?`)) return;
    await deleteDestination(d.id, d.imageUrl);
    toast.success('Removed'); load();
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-white font-bold text-xl">Featured Destinations</h2>
          <p className="text-[#546E7A] text-sm">
            These appear on the app home screen. Upload images to show real city photos.
          </p>
        </div>
        <button onClick={openAdd}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#D4AF37] hover:bg-[#A0832A]
            text-black rounded-xl text-sm font-semibold transition-colors">
          <Plus size={16} /> Add Destination
        </button>
      </div>

      {/* Info banner */}
      <div className="bg-[#2D5BE3]/10 border border-[#2D5BE3]/30 rounded-xl p-4 text-sm text-[#B0BEC5]">
        <strong className="text-white">How it works:</strong> Images uploaded here are stored in Firebase Storage
        and the URL is saved to Firestore. The Flutter app reads these URLs and displays the real photos
        in the Featured Destinations section on the home screen.
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#546E7A]">Loading...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {dests.map(d => (
            <div key={d.id} className="bg-[#1A2235] border border-[#1E2D45] rounded-2xl overflow-hidden group">
              {/* Image preview */}
              <div className="h-44 relative overflow-hidden"
                style={{ background: `linear-gradient(135deg, #${(d.color1 || 0xFF1A3C8F).toString(16).slice(2)}, #${(d.color2 || 0xFF2D5BE3).toString(16).slice(2)})` }}>
                {d.imageUrl ? (
                  <img src={d.imageUrl} alt={d.city} className="w-full h-full object-cover" />
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-white/40">
                    <MapPin size={32} />
                    <span className="text-xs mt-2">No image</span>
                  </div>
                )}
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <div className="absolute bottom-3 left-3">
                  <div className="text-white font-bold text-lg">{d.city}</div>
                  <div className="text-white/70 text-sm">{d.country}</div>
                </div>
                <div className="absolute top-2 right-2">
                  <span className="text-xs px-2 py-1 bg-[#D4AF37]/90 text-black rounded-full font-bold">
                    {d.price}
                  </span>
                </div>
                <div className="absolute top-2 left-2 text-white/40 text-xs">
                  Order: {d.order}
                </div>
              </div>
              <div className="p-3 flex items-center justify-between">
                <div className="text-xs text-[#546E7A]">
                  {d.imageUrl ? '✅ Image set' : '⚠️ No image — gradient shown'}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => openEdit(d)}
                    className="w-7 h-7 rounded-lg bg-[#2D5BE3]/10 hover:bg-[#2D5BE3]/20
                      flex items-center justify-center text-[#2D5BE3] transition-colors">
                    <Pencil size={13} />
                  </button>
                  <button onClick={() => remove(d)}
                    className="w-7 h-7 rounded-lg bg-red-500/10 hover:bg-red-500/20
                      flex items-center justify-center text-red-400 transition-colors">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-[#111827] border border-[#1E2D45] rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-[#1E2D45]">
              <h3 className="text-white font-bold">
                {modal === 'add' ? 'Add Featured Destination' : `Edit ${form.city}`}
              </h3>
              <button onClick={close} className="text-[#546E7A] hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4">
              <ImageUpload
                label="City Photo (shown on app home screen)"
                value={form.imageUrl}
                onChange={file => { setImageFile(file); if (!file) f('imageUrl', ''); }}
                progress={uploadProgress}
              />
              <div className="grid grid-cols-2 gap-3">
                {[
                  ['City *', 'city'], ['Country *', 'country'],
                  ['Price Label', 'price'], ['Display Order', 'order'],
                ].map(([label, key]) => (
                  <div key={key}>
                    <label className="block text-xs text-[#B0BEC5] mb-1">{label}</label>
                    <input value={form[key]} onChange={e => f(key, e.target.value)}
                      type={key === 'order' ? 'number' : 'text'}
                      className="w-full px-3 py-2 bg-[#1A2235] border border-[#1E2D45] rounded-xl
                        text-white text-sm focus:outline-none focus:border-[#D4AF37]" />
                  </div>
                ))}
              </div>
              <p className="text-xs text-[#546E7A]">
                Tip: Upload a high-quality city photo (400×300px recommended).
                It will be stored in Firebase Storage and shown in the app immediately.
              </p>
            </div>
            <div className="flex gap-3 p-5 border-t border-[#1E2D45]">
              <button onClick={close} className="flex-1 py-2.5 bg-[#1A2235] border border-[#1E2D45]
                text-[#B0BEC5] rounded-xl text-sm hover:text-white transition-colors">Cancel</button>
              <button onClick={save} disabled={saving}
                className="flex-1 py-2.5 bg-[#D4AF37] hover:bg-[#A0832A] text-black rounded-xl
                  text-sm font-semibold transition-colors disabled:opacity-50">
                {saving ? `Uploading... ${uploadProgress}%` : 'Save Destination'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
